import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * First-load brand splash — "{Product} · powered by Fulcrum5".
 *
 * Motion mirrors codify-web/src/Splash.tsx (animate-text soft-blur-in via WAAPI).
 * Shared in suite-ui so Platform / Source / Function / suite hosts do not drift.
 *
 * Timeline (~1.2s when not holding):
 *   t=0      product name soft-blur-in (per character)
 *   t=180    "powered by Fulcrum5" (per word; gold 5)
 *   t=900    onReveal + overlay fade
 *   t=1200   onDone
 *
 * `holding`: stay up after the enter timeline (e.g. suite context still loading
 * or redirecting to sign-in). When holding later becomes false, exit runs once.
 */

const ENTER_DUR = 500;
const ENTER_STAGGER = 45;
const ENTER_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const ENTER_FROM = { opacity: 0, y: 14, blur: 12 };

const SUB_DELAY = 180;
const SUB_DUR = 420;
const SUB_STAGGER = 26;
const SUB_FROM = { opacity: 0, y: 8, blur: 6 };

const EXIT_AT = 900;
const EXIT_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const TOTAL = 1200;

const SUBLINE = ["powered", "by", "Fulcrum"] as const;

export interface ProductSplashProps {
  /** Product wordmark, e.g. "Platform", "Source", "Function", "Codify". */
  readonly productName: string;
  readonly onReveal: () => void;
  readonly onDone: () => void;
  /**
   * Hold after enter (loading / sign-in redirect). No onReveal/onDone until
   * holding becomes false (or the parent unmounts).
   */
  readonly holding?: boolean;
  /** Label under the lockup while holding. */
  readonly holdLabel?: string;
}

export function ProductSplash({
  productName,
  onReveal,
  onDone,
  holding = false,
  holdLabel = "Opening",
}: ProductSplashProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const lockupRef = useRef<HTMLDivElement>(null);
  const mountAt = useRef(typeof performance !== "undefined" ? performance.now() : Date.now());
  const exitedRef = useRef(false);
  const [reduced] = useState(
    () =>
      typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );

  const cb = useRef({ onReveal, onDone });
  cb.current = { onReveal, onDone };
  const holdingRef = useRef(holding);
  holdingRef.current = holding;

  const reducedRef = useRef(reduced);
  reducedRef.current = reduced;

  const runExit = useRef(() => {
    if (exitedRef.current) return;
    if (holdingRef.current) return;
    exitedRef.current = true;
    const root = rootRef.current;
    root?.setAttribute("data-leaving", "");
    cb.current.onReveal();
    if (root && typeof root.animate === "function") {
      // Full motion: soft blur exit. Reduced motion: opacity only (Codify parity).
      const frames = reducedRef.current
        ? [{ opacity: 1 }, { opacity: 0 }]
        : [
          { opacity: 1, filter: "blur(0px)" },
          { opacity: 0, filter: "blur(6px)" },
        ];
      root.animate(frames, {
        duration: TOTAL - EXIT_AT,
        easing: EXIT_EASE,
        fill: "forwards",
      });
    } else if (root) {
      root.style.opacity = "0";
    }
    window.setTimeout(() => {
      if (holdingRef.current) return;
      cb.current.onDone();
    }, TOTAL - EXIT_AT);
  });

  useEffect(() => {
    const lockup = lockupRef.current;
    if (!lockup) return;

    if (reduced) {
      if (typeof lockup.animate === "function") {
        lockup.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 300, easing: "ease-out", fill: "forwards" },
        );
      } else {
        lockup.style.opacity = "1";
      }
      const exit = window.setTimeout(() => {
        if (holdingRef.current) return;
        runExit.current();
      }, 900);
      return () => {
        window.clearTimeout(exit);
      };
    }

    const units = Array.from(lockup.querySelectorAll<HTMLElement>("[data-unit]"));
    const anims: Animation[] = [];

    for (const el of units) {
      const isSub = el.dataset.role === "sub";
      const i = Number(el.dataset.i ?? 0);
      const from = isSub ? SUB_FROM : ENTER_FROM;
      const dur = isSub ? SUB_DUR : ENTER_DUR;
      const stagger = isSub ? SUB_STAGGER : ENTER_STAGGER;
      const base = isSub ? SUB_DELAY : 0;
      // jsdom has no Element.animate — resolve styles so tests still boot.
      if (typeof el.animate !== "function") {
        el.style.opacity = "1";
        el.style.transform = "none";
        el.style.filter = "none";
        continue;
      }
      const a = el.animate(
        [
          {
            opacity: from.opacity,
            transform: `translateY(${from.y}px)`,
            filter: `blur(${from.blur}px)`,
          },
          { opacity: 1, transform: "translateY(0)", filter: "blur(0px)" },
        ],
        {
          duration: dur,
          delay: base + i * stagger,
          easing: ENTER_EASE,
          fill: "both",
        },
      );
      a.onfinish = () => {
        el.style.opacity = "1";
        el.style.transform = "none";
        el.style.filter = "none";
        a.cancel();
      };
      anims.push(a);
    }

    const exitTimer = window.setTimeout(() => {
      runExit.current();
    }, EXIT_AT);

    return () => {
      anims.forEach((a) => a.cancel());
      window.clearTimeout(exitTimer);
    };
    // Mount-once timeline (callbacks via ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If we held through EXIT_AT, release as soon as holding clears.
  useEffect(() => {
    if (holding || exitedRef.current) return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - mountAt.current < EXIT_AT) return;
    runExit.current();
  }, [holding]);

  // Indeterminate hold bar (WAAPI transform only — suite CSS bans @keyframes).
  useEffect(() => {
    if (!holding || reduced) return;
    const bar = rootRef.current?.querySelector<HTMLElement>("[data-hold-bar-sliver]");
    if (!bar || typeof bar.animate !== "function") return;
    const a = bar.animate(
      [
        { transform: "translateX(-100%)" },
        { transform: "translateX(280%)" },
      ],
      { duration: 1100, easing: "cubic-bezier(0.4, 0, 0.2, 1)", iterations: Infinity },
    );
    return () => a.cancel();
  }, [holding, reduced]);

  let charIndex = 0;
  const name = productName.trim() || "Fulcrum";

  return (
    <div className="f5-suite-splash" ref={rootRef} role="status" aria-live="polite">
      <span className="f5-suite-splash__sr">
        {holding
          ? `${name}. ${holdLabel}.`
          : `${name} — powered by Fulcrum5. Loading.`}
      </span>
      <div className="f5-suite-splash__lockup" ref={lockupRef} aria-hidden="true">
        <div className="f5-suite-splash__wordmark">
          {name.split("").map((ch) => (
            <span
              className="f5-suite-splash__char"
              data-unit
              data-i={charIndex++}
              key={`w${charIndex}`}
            >
              {ch}
            </span>
          ))}
        </div>
        <div className="f5-suite-splash__sub">
          {SUBLINE.map((word, i) => (
            <span className="f5-suite-splash__word" data-unit data-role="sub" data-i={i} key={word}>
              {word}
            </span>
          ))}
          <span
            className="f5-suite-splash__word f5-suite-splash__five"
            data-unit
            data-role="sub"
            data-i={SUBLINE.length}
          >
            5
          </span>
        </div>
      </div>
      {holding ? (
        <div className="f5-suite-splash__hold" aria-hidden="true">
          <span className="f5-suite-splash__hold-label">{holdLabel}</span>
          <span className="f5-suite-splash__hold-bar">
            <span className="f5-suite-splash__hold-sliver" data-hold-bar-sliver />
          </span>
        </div>
      ) : null}
    </div>
  );
}

export interface BrandBootShellProps {
  readonly productName: string;
  /** When false, splash holds after enter until true. */
  readonly ready: boolean;
  readonly children: ReactNode;
  readonly holdLabel?: string;
}

/**
 * Wraps product boot: brand splash until `ready` and the enter timeline finish.
 */
export function BrandBootShell({
  productName,
  ready,
  children,
  holdLabel = "Opening",
}: BrandBootShellProps): JSX.Element {
  const [splashDone, setSplashDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const holding = !ready;
  const showSplash = !splashDone || holding;

  return (
    <>
      {showSplash ? (
        <ProductSplash
          holdLabel={holdLabel}
          holding={holding}
          onDone={() => setSplashDone(true)}
          onReveal={() => setRevealed(true)}
          productName={productName}
        />
      ) : null}
      <div
        className="f5-suite-boot-shell__app"
        data-revealed={revealed || ready ? "" : undefined}
        data-splash-active={showSplash ? "" : undefined}
      >
        {children}
      </div>
    </>
  );
}
